import * as React from 'react';
import type { JSXElement } from '@fluentui/react-utilities';

import { DecoratorNode } from '@fluentui-copilot/text-editor';
import type {
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from '@fluentui-copilot/text-editor';
import type { ChatInputTokenProps } from './ChatInputToken.types';
import { ChatInputToken } from './ChatInputToken';

export type SerializedChatInputTokenNode = Spread<
  { text: string; componentProps?: ChatInputTokenProps },
  SerializedLexicalNode
>;

export class ChatInputTokenNode extends DecoratorNode<JSXElement | null> {
  __text: string;
  __componentProps?: ChatInputTokenProps;

  static getType(): string {
    return 'token';
  }
  static clone(node: ChatInputTokenNode): ChatInputTokenNode {
    return new ChatInputTokenNode(node.__text, node.__componentProps, node.__key);
  }

  static importJSON(serializedNode: SerializedChatInputTokenNode): ChatInputTokenNode {
    return $createChatInputTokenNode(serializedNode.text, serializedNode.componentProps);
  }

  isIsolated(): boolean {
    return true;
  }
  isInline(): boolean {
    return true;
  }

  getTextContent(): string {
    return this.__text;
  }

  exportJSON(): SerializedChatInputTokenNode {
    return {
      text: this.__text,
      componentProps: this.__componentProps,
      type: ChatInputTokenNode.getType(),
      version: 1,
    };
  }

  constructor(text: string, componentProps?: ChatInputTokenProps, key?: NodeKey) {
    super(key);
    this.__text = text;
    this.__componentProps = componentProps;
  }

  createDOM(): HTMLElement {
    // eslint-disable-next-line no-restricted-globals
    return document.createElement('span');
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(_editor: LexicalEditor, config: EditorConfig): JSXElement | null {
    if (this.__componentProps?.children) {
      return <ChatInputToken {...this.__componentProps} />;
    }
    return <ChatInputToken {...this.__componentProps}>{this.__text}</ChatInputToken>;
  }
}

export function $createChatInputTokenNode(
  text: string,
  componentProps?: ChatInputTokenProps,
  key?: NodeKey,
): ChatInputTokenNode {
  return new ChatInputTokenNode(text, componentProps, key);
}

export function $isChatInputTokenNode(node: LexicalNode | null | undefined): node is ChatInputTokenNode {
  return node instanceof ChatInputTokenNode;
}
